/**
 * 风控中心 API
 */

import { httpClient } from './http'

export interface PaginatedResponse<T> {
  count: number
  results: T[]
}

export type EmployeeExportFilter = 'unbound_wechat' | 'never_logged_in' | 'inactive' | 'all'

export const EMPLOYEE_EXPORT_FILTER_OPTIONS: Array<{ value: EmployeeExportFilter; label: string }> = [
  { value: 'unbound_wechat', label: '未绑定微信（默认）' },
  { value: 'never_logged_in', label: '从未登录' },
  { value: 'inactive', label: '已停用' },
  { value: 'all', label: '全部在职' }
]

export interface FraudAlertListItem {
  id: number
  alert_type: string
  alert_type_display: string
  severity: string
  severity_display: string
  enterprise: number
  enterprise_name: string
  merchant: number | null
  merchant_name: string
  description: string
  estimated_loss: string
  status: string
  status_display: string
  affected_users_count: number
  affected_orders_count: number
  detected_at: string
  resolved_at: string | null
}

export interface FraudAlertDetail extends FraudAlertListItem {
  evidence: Record<string, unknown>
  resolved_by: number | null
  created_at: string
  updated_at: string
}

export interface DeviceLoginLogItem {
  id: number
  username: string
  /** 员工姓名（姓+名，空则回退用户名） */
  employee_name?: string
  enterprise_name: string
  login_at: string
  ip_address: string | null
  device_model: string
  os_name: string
  os_version: string
  device_fingerprint: string
  login_method: string
}

export interface FraudAlertListParams {
  page?: number
  page_size?: number
  enterprise_id?: string | number
  merchant_id?: string | number
  alert_type?: string
  severity?: string
  status?: string
}

export interface DeviceLoginLogListParams {
  page?: number
  page_size?: number
  /** 热数据天数：默认 7，最大 30 */
  days?: number
  /** 开始日期 YYYY-MM-DD（与 login_at_before 组合，列表跨度≤30天） */
  login_at_after?: string
  /** 结束日期 YYYY-MM-DD */
  login_at_before?: string
  /** 企业名称模糊（运营友好，优先于 enterprise_id） */
  enterprise_name?: string
  /** 员工姓名/用户名模糊（运营友好，优先于 user_id） */
  employee_keyword?: string
  enterprise_id?: string | number
  device_fingerprint?: string
  user_id?: string | number
  login_method?: string
}

export interface DeviceLoginLogExportParams {
  login_at_after: string
  login_at_before: string
  enterprise_name?: string
  employee_keyword?: string
  enterprise_id?: string | number
  device_fingerprint?: string
  user_id?: string | number
  login_method?: string
}

export async function listFraudAlerts(
  params?: FraudAlertListParams
): Promise<PaginatedResponse<FraudAlertListItem>> {
  return httpClient.get<PaginatedResponse<FraudAlertListItem>>('security/fraud-alerts/', { params })
}

export async function getFraudAlert(id: string | number): Promise<FraudAlertDetail> {
  return httpClient.get<FraudAlertDetail>(`security/fraud-alerts/${id}/`)
}

export async function updateFraudAlertStatus(
  id: string | number,
  status: string
): Promise<FraudAlertDetail> {
  return httpClient.post<FraudAlertDetail>(`security/fraud-alerts/${id}/update-status/`, { status })
}

export async function listDeviceLoginLogs(
  params?: DeviceLoginLogListParams
): Promise<PaginatedResponse<DeviceLoginLogItem>> {
  return httpClient.get<PaginatedResponse<DeviceLoginLogItem>>('security/device-login-logs/', { params })
}

/**
 * 按需导出设备登录日志 CSV（最长 31 天，与订单/流水明细一致）
 *
 * GET security/device-login-logs/export/
 */
export async function exportDeviceLoginLogs(
  params: DeviceLoginLogExportParams
): Promise<{ blob: Blob; filename: string }> {
  const fallback = `device_login_logs_${params.login_at_after}_${params.login_at_before}.csv`
  const resp = await httpClient.getAxiosInstance().get('security/device-login-logs/export/', {
    params,
    responseType: 'blob'
  })
  const disposition = resp.headers['content-disposition'] as string | undefined
  const filename = parseContentDispositionFilename(disposition, fallback)
  const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' })
  return { blob, filename }
}

function parseContentDispositionFilename(
  contentDisposition: string | undefined,
  fallback: string
): string {
  if (!contentDisposition) return fallback
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1])
    } catch {
      return utf8Match[1]
    }
  }
  const asciiMatch = contentDisposition.match(/filename="([^"]+)"/i)
  if (asciiMatch?.[1]) return asciiMatch[1]
  return fallback
}

/**
 * 导出企业员工清单（Excel）
 *
 * GET security/employees/export-unbound/
 * 优先 enterprise_name；兼容 enterprise_id；可选 employee_keyword
 */
export async function exportSecurityEmployees(
  params: {
    enterprise_name?: string
    enterprise_id?: string | number
    employee_keyword?: string
    filter?: EmployeeExportFilter
  } | string | number,
  filter?: EmployeeExportFilter
): Promise<{ blob: Blob; filename: string }> {
  // 兼容旧签名 exportSecurityEmployees(enterpriseId, filter)
  let query: Record<string, string | number> = {}
  if (typeof params === 'string' || typeof params === 'number') {
    query = { enterprise_id: params, filter: filter || 'unbound_wechat' }
  } else {
    if (params.enterprise_name) query.enterprise_name = params.enterprise_name
    if (params.enterprise_id != null && params.enterprise_id !== '') {
      query.enterprise_id = params.enterprise_id
    }
    if (params.employee_keyword) query.employee_keyword = params.employee_keyword
    query.filter = params.filter || 'unbound_wechat'
  }

  const fallbackKey = String(query.enterprise_name || query.enterprise_id || 'enterprise')
  const fallback = `${fallbackKey}_${query.filter}.xlsx`
  const resp = await httpClient.getAxiosInstance().get('security/employees/export-unbound/', {
    params: query,
    responseType: 'blob'
  })
  const disposition = resp.headers['content-disposition'] as string | undefined
  const filename = parseContentDispositionFilename(disposition, fallback)
  const blob = new Blob([resp.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  return { blob, filename }
}
